/* WNNRS measurement bridge for the existing ShopLift Grounding Hoodie test.
 *
 * Version-controlled source copy of the production grounding tracker.
 * Promote to the live theme only through an explicit theme release; never
 * edit the live theme asset directly.
 *
 * Attribution contract:
 * - Assignment comes ONLY from verified ShopLift visitor data
 *   (window.shoplift.getVisitorData().visitorTests or the Shoplift_Essential
 *   cookie). A visitorTests entry counts only when it identifies BOTH the
 *   test and the hypothesis. Reported IDs are always echoed from the matched
 *   entry, never from constants.
 * - The rendered DOM layout ([data-grounding-layout-a/b]) is recorded as the
 *   observational `rendered_layout` fact only. It NEVER backfills test or
 *   variant IDs, and preview/session data coupled to DOM observation does not
 *   count as assignment.
 * - Sessions without verified assignment stay unassigned: test/variant IDs
 *   are null with attribution_status "unassigned".
 */
(() => {
  const experimentId = "grounding-hoodie-layout";
  const ingestUrl = "https://cloudnine-experiment-platform-ingest.anagh-nair.workers.dev/v1/events";
  // ShopLift identifiers already observed for this test: the dashboard/API
  // test id plus the storefront-side test id seen in live ShopLift session
  // data. This set is ONLY an allowlist for matching verified visitorTests
  // entries below. No reported ID ever comes from these constants.
  const KNOWN_SHOPLIFT_TEST_IDS = new Set([
    "6e029380-9d88-43da-a848-a3ae06b5bd92",
    "01a08b26-4540-7004-b4cb-6e3559833254",
  ]);
  const KNOWN_SHOPLIFT_HYPOTHESIS_IDS = new Set([
    "01a08b14-f2ab-7748-b759-09960933d81b",
    "01a08b15-5b6c-768e-b933-5a997776cd6f",
  ]);
  const shop = window.Shopify?.shop || location.hostname;
  const sessionKey = "wnnrs_session_id";
  const sessionId = sessionStorage.getItem(sessionKey) || crypto.randomUUID();
  sessionStorage.setItem(sessionKey, sessionId);
  const sent = new Set();

  function readShopliftCookie(name) {
    const raw = document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith(`${name}=`));
    if (!raw) return null;
    try {
      const value = decodeURIComponent(raw.slice(name.length + 1));
      return JSON.parse(value.replace(/\\\\"/g, '"'));
    } catch (_) { return null; }
  }

  // A visitorTests entry is a verified assignment only when it names a known
  // test AND carries a hypothesis/variant identifier. Anything less (unknown
  // test, missing hypothesis, preview-only data) is not assignment.
  function findVerifiedAssignment(tests) {
    if (!Array.isArray(tests)) return null;
    const entry = tests.find((test) => {
      if (!test || typeof test !== "object") return false;
      const testId = String(test.testId || test.test_id || test.id || "");
      const hypothesisId = test.hypothesisId || test.hypothesis_id || test.variantId;
      return testId !== "" && KNOWN_SHOPLIFT_TEST_IDS.has(testId) &&
        hypothesisId !== undefined && hypothesisId !== null &&
        KNOWN_SHOPLIFT_HYPOTHESIS_IDS.has(String(hypothesisId));
    });
    if (!entry) return null;
    return {
      testId: String(entry.testId || entry.test_id || entry.id),
      hypothesisId: String(entry.hypothesisId || entry.hypothesis_id || entry.variantId),
    };
  }

  // Verified ShopLift assignment only. No DOM access here by design: rendered
  // layout must never influence attribution, so this function cannot see it.
  function attributionContext() {
    const unassigned = (visitor) => ({
      assigned: false,
      assignment_source: "none",
      attribution_status: "unassigned",
      exposure_source: "rendered_layout_observed",
      shoplift_test_id: null,
      shoplift_variant_id: null,
      shoplift_visitor_id: visitor?.id || null,
      shopify_analytics_id: visitor?.shopifyAnalyticsId || null,
    });
    try {
      const apiData = window.shoplift?.getVisitorData?.() || {};
      const essential = readShopliftCookie("Shoplift_Essential") || {};
      const analytics = readShopliftCookie("Shoplift_Analytics") || {};
      const apiAssignment = findVerifiedAssignment(apiData.visitorTests);
      const cookieAssignment = apiAssignment ? null : findVerifiedAssignment(essential.visitorTests);
      const assignment = apiAssignment || cookieAssignment;
      const visitor = apiData.visitor || analytics.visitor || {};
      if (!assignment) return unassigned(visitor);
      return {
        assigned: true,
        assignment_source: apiAssignment ? "shoplift:getVisitorData" : "shoplift:cookie",
        attribution_status: "assigned",
        exposure_source: "shoplift",
        shoplift_test_id: assignment.testId,
        shoplift_variant_id: assignment.hypothesisId,
        shoplift_visitor_id: visitor.id || null,
        shopify_analytics_id: visitor.shopifyAnalyticsId || null,
      };
    } catch (_) {
      return unassigned(null);
    }
  }

  function publish(eventName, properties, dedupeKey) {
    if (dedupeKey && sent.has(dedupeKey)) return;
    if (dedupeKey) sent.add(dedupeKey);
    const attribution = attributionContext();
    const payload = {
      event_id: crypto.randomUUID(),
      event_name: eventName,
      event_version: 1,
      occurred_at: new Date().toISOString(),
      store_id: shop,
      experiment_id: experimentId,
      variant_id: attribution.shoplift_variant_id,
      visitor_id: attribution.shoplift_visitor_id,
      session_id: sessionId,
      page_type: "product",
      page_path: location.pathname,
      properties: {
        source: "wnnrs_theme_tracker",
        ...properties,
        // Attribution block is authoritative here and always wins over caller
        // properties, so interaction call sites cannot inject inferred IDs.
        // rendered_layout is the observed DOM fact; assignment fields stay
        // null unless verified ShopLift data identified test + hypothesis.
        assignment_source: attribution.assignment_source,
        attribution_status: attribution.attribution_status,
        exposure_source: attribution.exposure_source,
        rendered_layout: layoutForContext(attribution),
        shoplift_test_id: attribution.shoplift_test_id,
        shoplift_variant_id: attribution.shoplift_variant_id,
        shoplift_visitor_id: attribution.shoplift_visitor_id,
        shopify_analytics_id: attribution.shopify_analytics_id,
      },
    };
    // The Web Pixel is the single network sender for theme custom events.
    // Publishing only here avoids sending the same event once directly and
    // once again after the app pixel receives the Shopify event.
    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(`cloudnine:${eventName}`, payload);
      }
    } catch (_) {
      // Measurement must never affect storefront behavior.
    }
  }

  window.CloudNineExperimentAnalytics = { publish };
  // Layout B has an explicit marker in its rendered template. Layout A is the
  // control/default template and has no marker on the live theme, so it is
  // considered observable only when verified ShopLift assignment identifies
  // the A hypothesis. This never turns an unassigned page into control.
  function layoutForContext(attribution) {
    if (document.querySelector("[data-grounding-layout-b]")) return "b";
    if (document.querySelector("[data-grounding-layout-a]")) return "a";
    if (attribution?.assigned && attribution.shoplift_variant_id === "01a08b14-f2ab-7748-b759-09960933d81b") return "a";
    if (attribution?.assigned && attribution.shoplift_variant_id === "01a08b15-5b6c-768e-b933-5a997776cd6f") return "b";
    return null;
  }

  const initialAttribution = attributionContext();
  const layout = layoutForContext(initialAttribution);
  if (!layout) return;

  publish("experiment_exposed", {
    exposure_key: `${experimentId}:${layout}:${location.pathname}`,
  }, `exposure:${layout}:${location.pathname}`);

  const componentNames = new Map([
    [".inline-bundle-builder", "bundle_builder"],
    [".tab-section", "feature_tabs"],
    [".cn-buybox-accordions", "buybox_accordions"],
    [".cn-size-overlay", "size_guide"],
  ]);
  if (window.IntersectionObserver) {
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const componentId = [...componentNames].find(([selector]) => entry.target.matches(selector))?.[1];
        if (componentId) publish("component_viewed", { component_id: componentId }, `component:${componentId}`);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.25 });
    for (const selector of componentNames.keys()) document.querySelectorAll(`${selector}`).forEach((node) => observer.observe(node));
  }

  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target.closest("button, a, [role=radio]") : null;
    if (!target || !target.closest("[data-grounding-layout-b], [data-grounding-layout-a]")) return;
    const component = target.closest(".inline-bundle-builder, .tab-section, .cn-size-overlay, .cn-buybox-accordions");
    const componentId = component?.matches(".inline-bundle-builder") ? "bundle_builder" :
      component?.matches(".tab-section") ? "feature_tabs" :
      component?.matches(".cn-size-overlay") ? "size_guide" :
      component?.matches(".cn-buybox-accordions") ? "buybox_accordions" : null;

    if (target.matches(".modal-offer-item")) {
      publish("option_selected", { option_id: target.id || target.dataset.bundleCount || "offer", option_group: "bundle_quantity", bundle_count: Number(target.dataset.bundleCount || 0) }, null);
      return;
    }
    if (target.matches(".swatch-item")) {
      publish("option_selected", { option_id: target.dataset.colorName || "color", option_group: "color" }, null);
      return;
    }
    if (target.matches(".tab-btn")) {
      publish("option_selected", { option_id: target.id || "tab", option_group: "feature_tab" }, null);
      return;
    }
    if (target.matches("[data-size-guide-trigger], .cn-size-close")) {
      publish("modal_opened", { modal_id: "size_guide" }, null);
      return;
    }

    const label = (target.getAttribute("aria-label") || target.textContent || "").trim().replace(/\s+/g, " ").slice(0, 120);
    if (!label) return;
    publish("button_clicked", { button_id: target.id || label, component_id: componentId, button_role: target.id === "modalConfirmBtn" ? "add_to_cart" : undefined }, null);
  }, true);
})();
